import { create } from 'zustand';
import { api } from '@/lib/apiClient';

function convertToArray(str) {
    if (!str) return [];
    return str.split('-').map(num => parseInt(num, 10));
}

export const useCityStore = create((set, get) => ({
    // State
    cities: [],
    localities: [],
    selectedCity: null,
    selectedLocalities: [],
    localitiesIds: [],
    localitiesCityId: null, // tracks which city the cached localities belong to
    citySearch: '',
    localitySearch: '',
    isLoading: false,

    // Simple setters
    setCitySearch: (citySearch) => set({ citySearch }),
    setLocalitySearch: (localitySearch) => set({ localitySearch }),
    setSelectedCity: (selectedCity) => set({ selectedCity }),
    setSelectedLocalities: (selectedLocalities) => set({ selectedLocalities }),
    addLocality: (locality) => set((state) => ({
        selectedLocalities: [...state.selectedLocalities, locality],
    })),
    removeLocality: (idToRemove) => set((state) => ({
        selectedLocalities: state.selectedLocalities.filter(l => l.id !== idToRemove),
    })),
    removeLastLocality: () => set((state) => ({
        selectedLocalities: state.selectedLocalities.slice(0, -1),
    })),

    // Async: Fetch localities for a given city
    fetchLocalities: async (cityId, initialLocalities = []) => {
        const { localities: currentLocalities, localitiesCityId } = get();

        const processLocalities = (localitiesList) => {
            const newState = { localities: localitiesList, localitiesCityId: cityId, localitySearch: '' };
            if (localitiesList.length > 0 && initialLocalities.length > 0) {
                const matched = localitiesList.filter(l => initialLocalities.includes(l.id));
                newState.selectedLocalities = matched.length > 0 ? matched : [];
            } else {
                newState.selectedLocalities = [];
            }
            set(newState);
        };

        // Use cache only if the localities are actually for the same city (strict type-safe check)
        if (cityId && parseInt(localitiesCityId, 10) === parseInt(cityId, 10) && currentLocalities && currentLocalities.length > 0) {
            processLocalities(currentLocalities);
            return;
        }

        try {
            const res = await api.post('selectwithout/master_localities', {
                sqlData: "locality_name as name, id, slug",
                where: "city_id=$1 and is_active=$2 order by name asc",
                whereData: [cityId, true],
            });
            if (res.success === 1 && Array.isArray(res.data)) {
                processLocalities(res.data);
            }
        } catch (error) {
            console.error('Error fetching localities:', error);
        }
    },

    // Async: Fetch cities and auto-select based on URL params
    fetchCities: async (cityIdParam, localitiesParam) => {
        const currentCities = get().cities;
        const localities = convertToArray(localitiesParam);
        set({ localitiesIds: localities });

        const processCities = (citiesList) => {
            if (citiesList.length > 0) {
                const { fetchLocalities, selectedCity: currentSelectedCity } = get();

                if (cityIdParam) {
                    // URL has a cityId — always sync to it
                    const city = citiesList.find(c => c.id === parseInt(cityIdParam, 10));
                    if (city) {
                        set({ selectedCity: city, citySearch: city.name });
                        fetchLocalities(city.id, localities);
                    }
                } else if (!currentSelectedCity) {
                    // No cityId in URL and nothing selected yet — pick the first city as default
                    const firstCity = citiesList[0];
                    set({ selectedCity: firstCity, citySearch: firstCity.name });
                    fetchLocalities(firstCity.id, localities);
                }
                // If no cityId in URL but a city is already selected, preserve the existing selection
            }
        };

        if (currentCities && currentCities.length > 0) {
            processCities(currentCities);
            return;
        }

        set({ isLoading: true });
        try {
            const response = await api.post('selectwithout/view_home_cities', {
                sqlData: "*",
                where: "is_active=$1 order by id asc",
                whereData: [true],
            });

            if (response.success === 1 && Array.isArray(response.data)) {
                set({ cities: response.data });
                processCities(response.data);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
        } finally {
            set({ isLoading: false });
        }
    },
}));